import React, { FormEvent } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Link } from 'react-router-dom';
import { AppState } from '../../../../store/store.types';

import { productEditRoles, ProductFilters, ProductModel } from '../../../../api/core/core.types';
import { ProductActionTypes } from '../../types/product.types';
import { getProductList, updateProductFilters } from '../../actions/product.actions';
import StatusLabel from '../../../../components/forms/StatusLabel';
import { UserModel } from '../../../../api/authentication/authentication.types';
import { userCanAccessComponent } from '../../../../helpers/authentication.helper';
import { defaultPageSize, PageRequest } from '../../../../api/api.types';
import { withTranslation, WithTranslation } from 'react-i18next';
import ListViewTable from '../../../../components/tables/ListViewTable';

interface ProductProps extends RouteComponentProps, WithTranslation {
  getProductList: (filters?: ProductFilters, pageRequest?: PageRequest) => void;
  updateProductFilters: (filters: ProductFilters) => void;
  products: ProductModel[] | null;
  user: UserModel | null;
  totalElements: number;
  totalPages: number;
  filters: ProductFilters | null;
}

interface ProductState {
  selectedPageNumber: number;
  sortOrder: 'ASC' | 'DESC';
  sortedBy: string;
  filtersUpdated: boolean;
  filter: {
    nameFilter: {
      value: string;
    };
  };
}

const ProductSortFields = [
  { value: 'code', label: 'CODE' }
];

class Products extends React.Component<ProductProps, ProductState> {
  constructor(props: ProductProps) {
    super(props);
    this.state = {
      selectedPageNumber: 1,
      sortOrder: 'ASC',
      sortedBy: 'CODE',
      filtersUpdated: false,
      filter: {
        nameFilter: {
          value: ''
        }
      }
    };
  }

  componentDidMount() {
    this.updateFilterValues();
    this.props.getProductList(this.props.filters ?? undefined);
  }

  componentDidUpdate() {
    if (!this.state.filtersUpdated) this.updateFilterValues();
  }

  onRowClick = (row: any) => this.props.history.push(`/setup/products/${row.original.id}/view`);

  updateFilterValues = () => {
    const filters = this.props.filters;
    this.setState({ filtersUpdated: true });

    if (filters) {
      this.setState({
        selectedPageNumber: filters.pageNumber ? filters.pageNumber : 1,
        filter: {
          nameFilter: {
            value: filters.name ? filters.name : ''
          }
        }
      });
    }
  };

  clearFilters = async () => {
    await this.setState({
      selectedPageNumber: 1,
      filtersUpdated: true,
      filter: {
        nameFilter: {
          value: ''
        }
      }
    });

    const filters = this.composeProductFilters();
    this.props.updateProductFilters(filters);
    this.props.getProductList();
  };

  storeFilterValues = () => {
    this.props.updateProductFilters({
      name: this.state.filter.nameFilter.value,
      pageNumber: this.state.selectedPageNumber
    });
  };

  statusCell = (props: any) => (<StatusLabel status={props}/>);

  onFilterClick = (e: FormEvent) => {
    e.preventDefault();
    const filters = this.composeProductFilters();

    this.props.updateProductFilters(filters);
    this.props.getProductList(filters, { page: this.state.selectedPageNumber - 1 });
  };

  changeHandler = (event: { target: HTMLInputElement }) => {
    const { name, value } = event.target;
    this.setState({
      filter: {
        ...this.state.filter,
        [ name ]: {
          value
        }
      }
    });
  };

  dropdownChange = (newValue: any, field: string) => {
    this.setState({
      ...this.state,
      filter: {
        ...this.state.filter,
        [ field ]: {
          value: newValue
        }
      }
    });
  };

  composeProductFilters = () => ({
    name: this.state.filter.nameFilter.value,
    pageNumber: this.state.selectedPageNumber - 1
  });


  defineColumns() {
    const { t } = this.props;

    return [
      {
        header: t('ct.products.code'), accessorKey: 'code', id: 'code'
      },
      {
        header: t('ct.products.name'), accessorKey: 'name', id: 'name'
      },
      {
        header: t('ct.products.description'), accessorKey: 'description', id: 'description'
      },
      {
        header: t('ct.products.status'),
        accessorKey: 'statusType',
        id: 'statusType',
        cell: (info: any) => this.statusCell(info.getValue())
      }
    ];
  }

  sortBy = (columnName: string, sortDirection: 'ASC' | 'DESC') => {
    const sortFieldName = ProductSortFields.find((column) => column.value === columnName);
    if (!sortFieldName) {
      return;
    }
    this.setState({ sortOrder: sortDirection, sortedBy: columnName });
    return (
      this.props.getProductList(this.composeProductFilters(), {
        page: this.state.selectedPageNumber - 1,
        size: defaultPageSize,
        sortField: sortFieldName.label,
        sortDirection: sortDirection
      })
    );
  };

  getDataFn(pageRequest: PageRequest) {
    if (pageRequest.page !== undefined && pageRequest.sortField !== undefined && pageRequest.sortDirection !== undefined) {
      this.props.getProductList(this.composeProductFilters(), pageRequest);

      this.setState({
        selectedPageNumber: pageRequest.page + 1,
        sortOrder: pageRequest.sortDirection,
        sortedBy: pageRequest.sortField
      });
    }
  }

  render() {
    const { t } = this.props;

    return (
      <main>
        <div className="page-content">
          <div className="d-flex justify-content-between align-items-center">
            <h1>{t('page_title.products_list')}</h1>
            {this.props.user && userCanAccessComponent(this.props.user, productEditRoles) && (
              <Link
                to={{
                  pathname: '/setup/products/create'
                }}
                className="btn btn-primary btn-wide"
              >
                {t('b.add_new').toUpperCase()}
              </Link>
            )}
          </div>
          <form className="d-flex justify-content-between container-fluid filter-container form-group form-inline">
            <div className="d-flex row ml-1 filters-container-margin-top-btm">
              <input
                type="text"
                value={this.state.filter.nameFilter.value}
                name="nameFilter"
                className="form-control mr-1"
                placeholder={t('ph.filter.products.by_name')}
                onChange={this.changeHandler}
              />
            </div>
            <div>
              <button type="button" className="btn btn-text-only mr-2" onClick={() => this.clearFilters()}>{
                t('b.clear_filter')
              }</button>
              <button type="submit" className="btn btn-primary filter-button" onClick={this.onFilterClick}>
                {t('b.filter').toUpperCase()}</button>
            </div>
          </form>
          {this.props.products
            ? (
              <div className="table-list">
                <ListViewTable
                  data={this.props.products}
                  columns={this.defineColumns()}
                  getTrProps={this.onRowClick}
                  sortBy={this.sortBy}
                  sortOrder={this.state.sortOrder}
                  sortedBy={this.state.sortedBy}
                  paginationEnabled={true}
                  totalPages={this.props.totalPages}
                  totalElements={this.props.totalElements}
                  selectedPageNumber={this.state.selectedPageNumber}
                  getDataFn={(pageRequest: PageRequest) => this.getDataFn(pageRequest)}
                />
              </div>
            ) : null}
        </div>
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  products: state.setup.products.products,
  user: state.authentication.user,
  totalElements: state.setup.products.totalElements,
  totalPages: state.setup.products.totalPages,
  filters: state.setup.products.filters
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, ProductActionTypes>) => ({
  getProductList: (filters?: ProductFilters, pageRequest?: PageRequest) => dispatch(getProductList(filters, pageRequest)),
  updateProductFilters: (filters: ProductFilters) => dispatch(updateProductFilters(filters))
});

export default withTranslation()(connect(
  mapStateToProps,
  mapDispatchToProps
)(Products));
